import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingController } from '@ionic/angular';
import { Observable, from, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { AppConfig, EndPoints } from '../config/config';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';
import { HttpBackEndService } from '../services/http-back-end-service.service';
import { HTMLIonOverlayElement } from '@ionic/core';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
	loadingController = inject(LoadingController);
	private router = inject(Router);
	private authService = inject(AuthService);
	private toast = inject(ToastService);
	private backendService = inject(HttpBackEndService);

	loaderToShow: any;
	isLoading: boolean = false;
	private token: string | null = '';

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		try {
			return from(this.modifyRequestAsync(request, next)).pipe(
				mergeMap(modifiedRequest => next.handle(modifiedRequest).pipe(
					map((event: HttpEvent<any>) => {
						if (event instanceof HttpResponse) { }
						this.backendService.setErrorRedirected(false);
						return event;
					}),
					catchError((err) => {
						//console.log('INTERCEPTED ERROR', err);

						let error: BackendError = { message: '', severity: ErrorSeverity.INFO, code: '' };

						if (err instanceof ErrorEvent) { // client side error
							error = this.handleUnknownError();
							this.toast.showToast('Error: ' + err.error.descripcion, 'error')
						} else { // server side error
							error = this.handleBackendError(error, err);
							if (err.status >= 500)
								this.toast.showToast('An error happened accessing the RAP server. Please try again later.', 'error')
							else if (err.status >= 400)

								if (err.status === 400) {}

								else if (err.status === 401) {
									if (err.error && err.error.message) {
									this.toast.showToast(err.error.message, 'error');
									} else {
									this.toast.showToast('You are not authorized to perform this action.', 'error');
									}
									this.authService.logOut(EndPoints.CLOSE_SESSION_UNAUTHORIZED);
								} 
								else if (err.status === 403) {
									this.toast.showToast('Your session expired, please log in again.', 'error');
									this.authService.logOut(EndPoints.CLOSE_SESSION_UNAUTHORIZED);
								}
								else if (err.status === 429) {
									this.backendService.setErrorRedirected(true);
									this.router.navigate([`/error/${err.status}`]);
								}
								else {
									this.toast.showToast('An error happened. Please try again later.', 'error');
								}
								
								// Remove loadings
								const overlays = document.querySelectorAll('ion-alert, ion-action-sheet, ion-loading, ion-modal, ion-picker, ion-popover, ion-toast');
								const overlaysArr = Array.from(overlays) as HTMLIonOverlayElement[];
								overlaysArr.forEach(o => {
									o.dismiss()
								});
						}
						return throwError(() => error);
					})
				))
			);
		} catch (error) {
			console.error('Error occurred in interceptor:', error);
			throw error;
		}
	}

	async modifyRequestAsync(request: HttpRequest<any>, next: HttpHandler): Promise<HttpRequest<any>> {
		return new Promise<HttpRequest<any>>(async (resolve, reject) => {
			try {
				// Fetch the token asynchronously
				this.token = await this.authService.getUserToken();
				//console.log('Token', this.token);


				// Modify the request headers based on the presence of the token
				let modifiedRequest: HttpRequest<any>;
				if (this.token) {
					modifiedRequest = request.clone({
						headers: request.headers
							.set('app-origin', AppConfig.APP_ORIGIN)
							.set('Authorization', `Bearer ${this.token}`)
							.set('Cache-Control', 'no-cache')
					});
				} else {
					modifiedRequest = request.clone({
						headers: request.headers
							.set('app-origin', AppConfig.APP_ORIGIN)
							.set('Cache-Control', 'no-cache')
					});
				}

				// Resolve the promise with the modified request
				resolve(modifiedRequest);
			} catch (error) {
				console.error('Error occurred while modifying request:', error);
				// Reject the promise with the error
				reject(error);
			}
		});
	}

	private handleUnknownError(): BackendError {
		// this is not from backend. Format our own message.
		return {
			message: 'Unknown error!',
			severity: ErrorSeverity.FATAL,
			code: 'UNKNOWN_ERROR',
		};
	}

	private handleBackendError(error: BackendError, err: any): BackendError {
		// Backend returned error, format it here
		return {
			title: err.error?.title || 'Error!',
			message:
				err.error && err.error.message
					? err.error.message
					: err.error
						? err.error
						: err.message,
			severity: ErrorSeverity.ERROR,
			code: err.error?.identifierCode
				? err.error.identifierCode
				: 'BACKEND_ERROR',
		};
	}
}

export enum ErrorSeverity {
	INFO = 'INFO',
	WARNING = 'WARNING',
	ERROR = 'ERROR',
	FATAL = 'FATAL',
}
export interface BackendError {
	title?: string;
	message: string;
	severity: ErrorSeverity;
	code: string;
}